import {
    Box,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    CheckboxMenuSearch,
    CheckboxMenuRow,
    CheckboxMenuList,
    CheckboxMenuFooter,
    CheckboxMenuTrigger,
    CheckboxMenuPinnedAll,
    SelectionSummaryTooltip,
    checkboxMenuPopoverContentSx,
} from '../ui/primitives/CheckboxMenu';

interface VendorSelectorProps {
    vendors: Array<string>;
    selectedVendors: Array<string> | 'all';
    onVendorChange: (vendors: Array<string> | 'all') => void;
    width?: string;
}

const normalizeVendors = (vendors: Array<string>): Array<string> => {
    return Array.from(
        new Set(
            vendors
                .map((vendor) => vendor.trim())
                .filter(Boolean)
        )
    ).sort((a, b) => a.localeCompare(b));
};

const VendorSelector = ({
    vendors,
    selectedVendors,
    onVendorChange,
    width = '220px',
}: VendorSelectorProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [popoverWidth, setPopoverWidth] = useState<number | string>(width);

    const normalizedVendors = useMemo(() => normalizeVendors(vendors), [vendors]);

    const allVendorsChecked = selectedVendors === 'all';
    const selectedIds = selectedVendors === 'all' ? [] : selectedVendors;

    const filteredVendors = normalizedVendors.filter((vendor) =>
        searchTerm === '' || vendor.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        if (selectedVendors === 'all') return;

        const validSelections = selectedVendors.filter((vendor) => normalizedVendors.includes(vendor));
        if (validSelections.length !== selectedVendors.length) {
            onVendorChange(validSelections);
        }
    }, [normalizedVendors, onVendorChange, selectedVendors]);

    useEffect(() => {
        if (isPopoverOpen && triggerRef.current) {
            setPopoverWidth(Math.max(triggerRef.current.offsetWidth, 260));
        }
    }, [isPopoverOpen]);

    useEffect(() => {
        if (!isPopoverOpen) {
            setSearchTerm('');
        }
    }, [isPopoverOpen]);

    if (normalizedVendors.length <= 1) {
        return null;
    }

    const selectedCount = allVendorsChecked ? normalizedVendors.length : selectedIds.length;

    // Past one vendor the chips would stack and grow the trigger, so collapse to a
    // count and move the names into the hover tooltip (same as LocationSelector).
    const isSubsetSelected = !allVendorsChecked && selectedIds.length > 1;
    const triggerTokens = allVendorsChecked
        ? []
        : isSubsetSelected
            ? [`${selectedIds.length} selected`]
            : selectedIds;

    const handleRemoveVendor = (vendor: string) => {
        const next = selectedIds.filter((v) => v !== vendor);
        onVendorChange(next.length === 0 ? 'all' : next);
    };

    return (
        <Popover
            placement="bottom-end"
            isOpen={isPopoverOpen}
            onOpen={() => setIsPopoverOpen(true)}
            onClose={() => setIsPopoverOpen(false)}
            gutter={8}
        >
            <PopoverTrigger>
                <Box w={width} ref={triggerRef}>
                    <SelectionSummaryTooltip
                        items={selectedIds}
                        isDisabled={!isSubsetSelected || isPopoverOpen}
                        fillParent
                    >
                        <CheckboxMenuTrigger
                            isOpen={isPopoverOpen}
                            fillToken
                            tokens={triggerTokens}
                            emptyLabel={allVendorsChecked ? 'All Vendors' : (selectedIds.length === 0 ? 'No Vendors Selected' : 'Select Vendors')}
                            onRemoveToken={
                                isSubsetSelected || allVendorsChecked
                                    ? undefined
                                    : (index: number) => {
                                        const vendor = selectedIds[index];
                                        if (vendor) handleRemoveVendor(vendor);
                                    }
                            }
                        />
                    </SelectionSummaryTooltip>
                </Box>
            </PopoverTrigger>
            <PopoverContent
                w={typeof popoverWidth === 'number' ? `${popoverWidth}px` : popoverWidth}
                sx={checkboxMenuPopoverContentSx}
            >
                <PopoverBody p={0}>
                    <CheckboxMenuSearch
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder="Search vendors..."
                    />
                    <CheckboxMenuPinnedAll
                        isSelected={allVendorsChecked}
                        onClick={() => { if (!allVendorsChecked) onVendorChange('all'); }}
                    >
                        All Vendors
                    </CheckboxMenuPinnedAll>
                    <CheckboxMenuList>
                        {filteredVendors.map((vendor) => (
                            <CheckboxMenuRow
                                key={vendor}
                                isSelected={!allVendorsChecked && selectedIds.includes(vendor)}
                                onClick={() => {
                                    if (allVendorsChecked) {
                                        onVendorChange([vendor]);
                                        return;
                                    }
                                    const next = selectedIds.includes(vendor)
                                        ? selectedIds.filter((v) => v !== vendor)
                                        : [...selectedIds, vendor];
                                    onVendorChange(next.length === 0 ? 'all' : next);
                                }}
                            >
                                {vendor}
                            </CheckboxMenuRow>
                        ))}
                    </CheckboxMenuList>
                    <CheckboxMenuFooter
                        selectedCount={selectedCount}
                        totalCount={normalizedVendors.length}
                        onClear={() => onVendorChange('all')}
                    />
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default VendorSelector;
